import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, withRouter } from 'react-router';

import EditFormPage from 'modules/ui/editform/EditFormPage';
import EditFormTextField from 'modules/ui/editform/fields/EditFormTextField';
import EditFormSelectField from 'modules/ui/editform/fields/EditFormSelectField';
import EditFormGroupFields from 'modules/ui/editform/subForm/EditFormGroupFields';
import EditFormSeparatorField from 'modules/ui/editform/fields/EditFormSeparatorField';
import EditFormGridComponent from 'modules/ui/editform/subForm/EditFormGridComponent';
import EditFormComponentMassSomethingField from 'app/admin/common/component/EditFormComponentMassSomethingField';

import StorageTaskManagerService from 'app/admin/taskManager/service/StorageTaskManagerService';
import MeasurementService from 'app/admin/measurement/service/MeasurementService';
import OptimizationCaseService from 'app/admin/optimizationCase/service/OptimizationCaseService';
import UnitOptimizerLogService from 'app/admin/unitOptimizerLog/service/UnitOptimizerLogService';
import WhatIfService from 'app/admin/whatIf/service/WhatIfService';
import UnitOptimizerEditService from '../service/UnitOptimizerEditService';
import UserGroupService from 'app/admin/userGroup/service/UserGroupService';

const blockClassName = 'rounded-8 border px-16 pt-10 pb-4 mb-16';
const blockTitleClassName = 'py-8';

const config = data => {
	const storageTaskManagerService = new StorageTaskManagerService();

	const form = {
		key: 'unitOptimizerEditForm',
		topic: 'unitOptimizer',
		service: UnitOptimizerEditService,
		fields: initData => {
			const fields = [
				{
					key: 'name',
					component: EditFormTextField,
					required: true,
					readOnly: data.id !== 'new'
				},
				{
					key: 'userGroup',
					component: EditFormSelectField,
					// multiple: true,
					items: () => UserGroupService.getListSelection()
				},
				{
					key: 'measurement',
					component: EditFormSelectField,
					required: true,
					items: () => MeasurementService.getListSelection()
				},
				{
					key: 'optimizationCase',
					component: EditFormSelectField,
					required: true,
					items: () => OptimizationCaseService.getListSelection()
				},
				{
					key: 'unitOptimizerLog',
					component: EditFormSelectField,
					required: false,
					items: () => UnitOptimizerLogService.getListSelection()
				},
				{
					key: 'whatIf',
					component: EditFormSelectField,
					required: false,
					items: () => WhatIfService.getListSelection()
				},
				{
					key: 'uom',
					component: EditFormTextField,
				},
					{
					key: 'cumulativeUom',
					component: EditFormTextField,
				},
				{
					key: 'multiplier',
					component: EditFormTextField,
					type: 'number'
				},
				{
					key: 'stateInjectGroup',
					component: EditFormGroupFields,
					className: blockClassName,
					defaultValue: {
						name: null,
					},
					dependsOn: 'measurement',
					visibleIf: measurement => measurement !== null,
					fields: [
						{
							key: 'sepStateInject',
							text: 'State Inject',
							component: EditFormSeparatorField,
							className: blockTitleClassName + ' pb-16',
							isControlledInput: false
						},
						{
							key: 'stateInject',
							component: EditFormGridComponent,
							fields: [
								{
									key: 'name',
									component: EditFormSelectField,
									required: true,
									type: 'enum',
									items: (dto, field) => storageTaskManagerService.getNamesForOptimizationCaseAspect(dto, field, 'inputs'),
									dependsOn: 'optimizationCase',
									visibleIf: optimizationCase => optimizationCase !== null,
								},
								{
									key: 'type',
									component: EditFormSelectField,
									required: true,
									type: 'enum',
									items: [
										{ label: 'Variable', value: 'Variable' },
										{ label: 'ComponentMoleFlow', value: 'ComponentMoleFlow' },
										{ label: 'ComponentMassFrac', value: 'ComponentMassFrac' },
										{ label: 'ComponentMassFlow', value: 'ComponentMassFlow' },
										{ label: 'DistillationCurve', value: 'DistillationCurve' },
										{ label: 'SpreadSheet', value: 'SpreadSheet' }
									]
								},
								{
									key: 'value',
									component: EditFormTextField,
									required: true,
									type: 'number',
									dependsOn: 'type',
									visibleIf: type => type === 'Variable' || type === 'SpreadSheet' || type === 'DistillationCurve' || type === 'ComponentMoleFlow'
								},
								{
									key: 'value',
									component: EditFormComponentMassSomethingField,
									required: true,
									dependsOn: 'type',
									visibleIf: type => type === 'ComponentMassFrac' || type === 'ComponentMassFlow'
								}
							]
						}
					]
				}
			];
			return fields;
		}
	};

	return form;
};

function UnitOptimizerEditForm() {
	const { id } = useParams();
	const myConfig = config({ id });

	// +++ új: autokitöltéshez szükséges állapotok + refek
	const [dto, setDto] = useState(null);
	const taskManager = useMemo(() => new StorageTaskManagerService(), []);
	const methodsRef = useRef(null);
	const fillingRef = useRef(false);

	useEffect(() => {
		let cancelled = false;

		async function fillTypes() {
			if (fillingRef.current) return;

			const oc = dto?.optimizationCase;
			if (!oc) return;

			const list = Array.isArray(dto?.stateInject) ? dto.stateInject : [];
			if (list.length === 0) return;

			const aspectId = await taskManager.getAspectIdForOptimizationCase(oc);
			if (!aspectId) return;

			fillingRef.current = true;
			try {
				for (let i = 0; i < list.length; i++) {
					if (cancelled) break;

					const si = list[i];
					const path = `stateInject.${i}.type`;

					if (!si || !si.name) {
						const current = methodsRef.current?.getValues(path);
						if (current !== null) {
							methodsRef.current?.setValue(path, null, {
								shouldDirty: true,
								shouldTouch: false,
								shouldValidate: false,
							});
						}
						continue;
					}

					const t = await taskManager.getTypeForAspectInput(si.name, aspectId, 'inputs');
					const newType = t || null;
					const current = methodsRef.current?.getValues(path);
					if (current !== newType) {
						methodsRef.current?.setValue(path, newType, {
							shouldDirty: true,
							shouldTouch: false,
							shouldValidate: false,
						});
					}
				}
			} finally {
				fillingRef.current = false;
			}
		}

		fillTypes();
		return () => { cancelled = true; fillingRef.current = false; };
	}, [
		dto?.optimizationCase,
		dto?.stateInject ? JSON.stringify(dto.stateInject.map(si => si?.name ?? null)) : null,
		taskManager
	]);

	return (
		<EditFormPage
			config={myConfig}
			onWatch={setDto}
			onDataChanged={(data, methods) => { methodsRef.current = methods; }}
		/>
	);
}

export default withRouter(UnitOptimizerEditForm);